import { createAction, props } from '@ngrx/store';

import type { ApplicationType as Application, StorageDataSort } from '@evc/platform';

import type { BackendError } from '@app/types/error/backend-error.type';
import type { FileItem } from '@app/types/file-item.type';
import type { Metadata } from '@app/types/nde-item-metadata.type';

export const LOAD_DATA = '[File] Load data';
export const LOAD_DATA_SUCCESS = '[File] Load data success';
export const LOAD_DATA_FAIL = '[File] Load data fail';

export const SET_SEARCH = '[File] Set search';
export const CLEAR_SEARCH = '[File] Clear search';

export const LOAD_FOLDER = '[File] Load folder';
export const LOAD_FOLDER_SUCCESS = '[File] Load folder success';
export const LOAD_FOLDER_FAIL = '[File] Load folder fail';

export const SET_SELECTED_ITEMS = '[File] Set selected items';
export const SET_CURRENT_FOLDER = '[File] Set current folder';
export const SET_NEW_ID_TO_FIND = '[File] Set new ID to find';

export const CREATE_FOLDER = '[File] Create folder';
export const CREATE_FOLDER_SUCCESS = '[File] Create folder success';
export const CREATE_FOLDER_FAIL = '[File] Create folder fail';

export const RENAME_ITEM = '[File] Rename item';
export const RENAME_ITEM_SUCCESS = '[File] Rename item success';
export const RENAME_ITEM_FAIL = '[File] Rename item fail';

export const DELETE_ITEMS = '[File] Delete items';
export const DELETE_ITEMS_SUCCESS = '[File] Delete items success';
export const DELETE_ITEMS_FAIL = '[File] Delete items fail';

export const DELETE_ALL_TRASH = '[File] Delete all trash';
export const DELETE_ALL_TRASH_SUCCESS = '[File] Delete all trash success';
export const DELETE_ALL_TRASH_FAIL = '[File] Delete all trash fail';

export const PERMANENTLY_DELETE_ITEMS = '[File] Permanently delete items';
export const PERMANENTLY_DELETE_ITEMS_SUCCESS = '[File] Permanently delete items success';
export const PERMANENTLY_DELETE_ITEMS_FAIL = '[File] Permanently delete items fail';

export const RESTORE_ITEMS = '[File] Restore items';
export const RESTORE_ITEMS_SUCCESS = '[File] Restore items success';
export const RESTORE_ITEMS_FAIL = '[File] Restore items fail';

export const OPEN_FILE = '[File] Open file';
export const OPEN_IN_APP = '[File] Open in app';
export const OPEN_IN_APP_FAIL = '[File] Open in app fail';

export const PREVIEW_FILE = '[File] Preview file';
export const PREVIEW_FILE_FAIL = '[File] Preview file fail';

export const DOWNLOAD_ITEMS = '[File] Download items';
export const DOWNLOAD_ITEM_PROGRESS = '[File] Download item progress';
export const DOWNLOAD_ITEM_SUCCESS = '[File] Download item success';
export const DOWNLOAD_ITEM_FAIL = '[File] Download item fail';

export const DOWNLOAD_ITEMS_BY_URL = '[File] Download items by url';
export const DOWNLOAD_ITEMS_BY_URL_SUCCESS = '[File] Download items by url success';
export const DOWNLOAD_ITEMS_BY_URL_FAIL = '[File] Download items by url fail';

export const COPY_ITEMS = '[File] Copy items';
export const COPY_ITEMS_SUCCESS = '[File] Copy items success';
export const COPY_ITEMS_FAIL = '[File] Copy items fail';

export const MOVE_ITEMS = '[File] Move items';
export const MOVE_ITEMS_SUCCESS = '[File] Move items success';
export const MOVE_ITEMS_FAIL = '[File] Move items fail';

export const LOCK_ITEMS = '[File] Lock items';
export const LOCK_ITEMS_SUCCESS = '[File] Lock items success';
export const LOCK_ITEMS_FAIL = '[File] Lock items fail';

export const UNLOCK_ITEMS = '[File] Unlock items';
export const UNLOCK_ITEMS_SUCCESS = '[File] Unlock items success';
export const UNLOCK_ITEMS_FAIL = '[File] Unlock items fail';

export const START_DEFECT_ANALYSIS = '[File] Start defect analysis';
export const START_DEFECT_ANALYSIS_SUCCESS = '[File] Start defect analysis success';
export const START_DEFECT_ANALYSIS_FAIL = '[File] Start defect analysis fail';

export const START_ANOMALY_ANALYSIS = '[File] Start anomaly analysis';
export const START_ANOMALY_ANALYSIS_SUCCESS = '[File] Start anomaly analysis success';
export const START_ANOMALY_ANALYSIS_FAIL = '[File] Start anomaly analysis fail';

export const LOAD_ITEM_METADATA = '[File] Load item metadata';
export const LOAD_ITEM_METADATA_SUCCESS = '[File] Load item metadata success';
export const LOAD_ITEM_METADATA_FAIL = '[File] Load item metadata fail';
export const CLEAR_METADATA = '[File] clear metadata';

export const RESET_FILE_STATE = '[File] Reset state';

export const loadData: any = createAction(
  LOAD_DATA,
  props<{ page: number; sort: StorageDataSort; search: string }>(),
);

export const loadDataSuccess: any = createAction(
  LOAD_DATA_SUCCESS,
  props<{
    data: FileItem[];
    page: number;
    count: number;
    sort: StorageDataSort;
    idToFind?: string;
  }>(),
);

export const loadDataFail: any = createAction(LOAD_DATA_FAIL, props<{ error: BackendError }>());

export const setSearch: any = createAction(SET_SEARCH, props<{ search: string }>());

export const clearSearch: any = createAction(CLEAR_SEARCH);

export const loadFolder: any = createAction(LOAD_FOLDER, props<{ id: string }>());

export const loadFolderSuccess: any = createAction(
  LOAD_FOLDER_SUCCESS,
  props<{ folder: FileItem }>(),
);

export const loadFolderFail: any = createAction(LOAD_FOLDER_FAIL, props<{ error: BackendError }>());

export const setSelectedItems: any = createAction(
  SET_SELECTED_ITEMS,
  props<{ items: FileItem[] }>(),
);

export const setCurrentFolder: any = createAction(
  SET_CURRENT_FOLDER,
  props<{ folder: FileItem }>(),
);

export const setNewIdToFind: any = createAction(SET_NEW_ID_TO_FIND, props<{ idToFind: string }>());

export const createFolder: any = createAction(CREATE_FOLDER, props<{ name: string }>());

export const createFolderSuccess: any = createAction(
  CREATE_FOLDER_SUCCESS,
  props<{ folder: FileItem }>(),
);

export const createFolderFail: any = createAction(
  CREATE_FOLDER_FAIL,
  props<{ error: BackendError }>(),
);

export const renameItem: any = createAction(RENAME_ITEM, props<{ item: FileItem }>());

export type RenameItemSuccessProps = {
  item: FileItem;
  previous: FileItem;
};
export const renameItemSuccess: any = createAction(
  RENAME_ITEM_SUCCESS,
  props<RenameItemSuccessProps>(),
);

export const renameItemFail: any = createAction(
  RENAME_ITEM_FAIL,
  props<{
    error: BackendError;
    item?: FileItem;
  }>(),
);

export const deleteItems: any = createAction(DELETE_ITEMS, props<{ items: FileItem[] }>());

export const deleteItemsSuccess: any = createAction(
  DELETE_ITEMS_SUCCESS,
  props<{ items: FileItem[] }>(),
);

export const deleteItemsFail: any = createAction(
  DELETE_ITEMS_FAIL,
  props<{ error: BackendError }>(),
);

export const deleteAllTrash: any = createAction(DELETE_ALL_TRASH);

export const deleteAllTrashSuccess: any = createAction(
  DELETE_ALL_TRASH_SUCCESS,
  props<{ totalDeletedFiles: number; totalDeletedDirectories: number }>(),
);

export const deleteAllTrashFail: any = createAction(
  DELETE_ALL_TRASH_FAIL,
  props<{ error: BackendError }>(),
);

export const permanentlyDeleteItems: any = createAction(
  PERMANENTLY_DELETE_ITEMS,
  props<{ items: FileItem[] }>(),
);

export const permanentlyDeleteItemsSuccess: any = createAction(
  PERMANENTLY_DELETE_ITEMS_SUCCESS,
  props<{ items: FileItem[] }>(),
);

export const permanentlyDeleteItemsFail: any = createAction(
  PERMANENTLY_DELETE_ITEMS_FAIL,
  props<{ error: BackendError }>(),
);

export const restoreItems: any = createAction(
  RESTORE_ITEMS,
  props<{ items: FileItem[]; undo?: boolean }>(),
);

export const restoreItemsSuccess: any = createAction(
  RESTORE_ITEMS_SUCCESS,
  props<{ items: FileItem[]; undo?: boolean; toNotify?: FileItem[]; location?: string }>(),
);

export const restoreItemsFail: any = createAction(
  RESTORE_ITEMS_FAIL,
  props<{ error: BackendError }>(),
);

export const openFile: any = createAction(OPEN_FILE, props<{ file: FileItem; app: Application }>());

export const openInApp: any = createAction(
  OPEN_IN_APP,
  props<{ file: FileItem; app: Application }>(),
);

export const openInAppFail: any = createAction(OPEN_IN_APP_FAIL, props<{ error: BackendError }>());

export const previewFile: any = createAction(PREVIEW_FILE, props<{ file: FileItem }>());

export const previewFileFail: any = createAction(
  PREVIEW_FILE_FAIL,
  props<{ error: BackendError }>(),
);

export const downloadItems: any = createAction(DOWNLOAD_ITEMS, props<{ items: FileItem[] }>());

export const downloadItemProgress: any = createAction(
  DOWNLOAD_ITEM_PROGRESS,
  props<{ item: FileItem; percentage: number }>(),
);

export const downloadItemSuccess: any = createAction(
  DOWNLOAD_ITEM_SUCCESS,
  props<{ item: FileItem }>(),
);

export const downloadItemFail: any = createAction(
  DOWNLOAD_ITEM_FAIL,
  props<{ error: string; item?: FileItem; params?: Record<string, string> }>(),
);

export const downloadItemsByUrl: any = createAction(
  DOWNLOAD_ITEMS_BY_URL,
  props<{ items: FileItem[] }>(),
);

export const downloadItemsByUrlSuccess: any = createAction(
  DOWNLOAD_ITEMS_BY_URL_SUCCESS,
  props<{ items: FileItem[] }>(),
);

export const downloadItemsByUrlFail: any = createAction(
  DOWNLOAD_ITEMS_BY_URL_FAIL,
  props<{ error: BackendError }>(),
);

export const copyItems: any = createAction(
  COPY_ITEMS,
  props<{ items: FileItem[]; parentId?: string; prompt?: boolean }>(),
);

export const copyItemsSuccess: any = createAction(
  COPY_ITEMS_SUCCESS,
  props<{ items: FileItem[]; parentId: string; prompt?: boolean }>(),
);

export const copyItemsFail: any = createAction(COPY_ITEMS_FAIL, props<{ error: BackendError }>());

export const moveItems: any = createAction(
  MOVE_ITEMS,
  props<{ items: FileItem[]; parentId?: string; prompt?: boolean }>(),
);

export const moveItemsSuccess: any = createAction(
  MOVE_ITEMS_SUCCESS,
  props<{ items: FileItem[] }>(),
);

export const moveItemsFail: any = createAction(MOVE_ITEMS_FAIL, props<{ error: BackendError }>());

export const resetFileState: any = createAction(RESET_FILE_STATE);

export const lockItems: any = createAction(LOCK_ITEMS, props<{ items: FileItem[] }>());

export const lockItemsSuccess: any = createAction(
  LOCK_ITEMS_SUCCESS,
  props<{ items: FileItem[] }>(),
);

export const lockItemsFail: any = createAction(LOCK_ITEMS_FAIL, props<{ error: BackendError }>());

export const unlockItems: any = createAction(UNLOCK_ITEMS, props<{ items: FileItem[] }>());

export const unlockItemsSuccess: any = createAction(
  UNLOCK_ITEMS_SUCCESS,
  props<{ items: FileItem[] }>(),
);

export const unlockItemsFail: any = createAction(
  UNLOCK_ITEMS_FAIL,
  props<{ error: BackendError }>(),
);

export const startDefectAnalysis: any = createAction(
  START_DEFECT_ANALYSIS,
  props<{ item: FileItem }>(),
);

export const startDefectAnalysisSuccess: any = createAction(
  START_DEFECT_ANALYSIS_SUCCESS,
  props<{ item: FileItem }>(),
);

export const startDefectAnalysisFail: any = createAction(
  START_DEFECT_ANALYSIS_FAIL,
  props<{ error: BackendError }>(),
);

export const startAnomalyAnalysis: any = createAction(
  START_ANOMALY_ANALYSIS,
  props<{ item: FileItem }>(),
);

export const startAnomalyAnalysisSuccess: any = createAction(
  START_ANOMALY_ANALYSIS_SUCCESS,
  props<{ item: FileItem[] }>(),
);

export const startAnomalyAnalysisFail: any = createAction(
  START_ANOMALY_ANALYSIS_FAIL,
  props<{ error: BackendError }>(),
);

export const loadItemMetadata: any = createAction(LOAD_ITEM_METADATA, props<{ item: FileItem }>());

export const loadItemMetadataSuccess: any = createAction(
  LOAD_ITEM_METADATA_SUCCESS,
  props<{ metadata: Metadata }>(),
);

export const loadItemMetadataFail: any = createAction(
  LOAD_ITEM_METADATA_FAIL,
  props<{ error: BackendError; id: string }>(),
);

export const clearMetadata: any = createAction(CLEAR_METADATA);
